import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSearch } from '@/contexts/SearchContext';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { useCallback } from 'react';
const heroImage = '/images/lumza-hero.webp';
import HeroParticles from '@/components/HeroParticles';
import { z } from 'zod';

const searchSchema = z.string().trim().max(100);

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } }
};

const HeroSection = () => {
  const { t } = useLanguage();
  const { setSearchQuery: setGlobalSearchQuery } = useSearch();
  const navigate = useNavigate();

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const contentX = useTransform(mouseX, [-1, 1], [-8, 8]);
  const contentY = useTransform(mouseY, [-1, 1], [-6, 6]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width * 2 - 1;
      const y = (e.clientY - rect.top) / rect.height * 2 - 1;
      mouseX.set(x);
      mouseY.set(y);
    },
    [mouseX, mouseY]
  );

  const handleQuickSearch = (term: string) => {
    try {
      const validated = searchSchema.parse(term);
      setGlobalSearchQuery(validated);
      navigate(`/search?q=${encodeURIComponent(validated)}`);
    } catch (error) {
      console.error('Invalid search input');
    }
  };

  const scrollToStores = () => {
    document.querySelector('#stores-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="relative min-h-[60vh] sm:min-h-[65vh] md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-20 sm:pt-28 md:pt-32 lg:pt-36 pb-8 sm:pb-12"
      onMouseMove={handleMouseMove}>
      
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          alt="LUMZA After-Hours Delivery"
          className="w-full h-full object-cover object-center"
          loading="eager"
          fetchPriority="high" />
        
        <div className="absolute inset-0 bg-gradient-to-r from-midnight/95 via-midnight/70 to-midnight/40 sm:from-midnight/90 sm:via-midnight/60 sm:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight/90 via-midnight/40 to-transparent sm:from-midnight/80 sm:via-transparent" />
      </div>

      {/* Animated Particles Layer */}
      <HeroParticles />

      {/* Content */}
      <motion.div
        className="relative z-10 container mx-auto px-4 sm:px-6 text-center max-w-5xl"
        style={{ x: contentX, y: contentY }}
        variants={stagger}
        initial="hidden"
        animate="visible">
        
        <div className="space-y-6 sm:space-y-8">
          {/* Main Heading */}
          <div className="space-y-2 sm:space-y-3 md:space-y-4">
            <motion.h1 className="font-bold leading-tight" variants={fadeUp}>
              <span className="hero-shimmer-text sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-9xl block drop-shadow-lg tracking-tight font-bold text-center text-8xl">
                LUMZA
              </span>
              <span className="text-moonlight text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl font-semibold block mt-1 sm:mt-2 drop-shadow-md">
                After hours? We deliver.
              </span>
            </motion.h1>
            <motion.p
              className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-muted-foreground max-w-xl sm:max-w-2xl mx-auto leading-relaxed px-2 sm:px-4"
              variants={fadeUp}>
              
              {t('hero.subtitle')}{' '}
              <span className="text-neon font-semibold">11 PM - 7 AM</span>
            </motion.p>
          </div>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 justify-center items-stretch sm:items-center px-2 sm:px-4 max-w-xs sm:max-w-md md:max-w-lg mx-auto"
            variants={fadeUp}>
            
            <Button
              onClick={scrollToStores}
              className="btn-neon w-full sm:w-auto px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base md:text-lg h-10 sm:h-11 md:h-12 font-semibold touch-target">
              
              {t('hero.cta.primary')}
            </Button>
            <Button
              onClick={scrollToStores}
              variant="outline"
              className="btn-ghost-neon w-full sm:w-auto px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base md:text-lg h-10 sm:h-11 md:h-12 font-semibold touch-target">
              
              {t('hero.cta.secondary')}
            </Button>
          </motion.div>
        </div>
      </motion.div>

      {/* Floating Elements - Hidden on smallest screens */}
      <div className="hidden sm:block absolute top-16 md:top-20 left-6 md:left-10 w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-full blur-xl animate-pulse" />
      <div className="hidden sm:block absolute bottom-16 md:bottom-20 right-6 md:right-10 w-24 h-24 md:w-32 md:h-32 bg-primary/5 rounded-full blur-2xl float-animation" />
    </section>);

};

export default HeroSection;